export const CAMPAIGN_TEMPLATE_IDS = ['campaign-companies', 'campaign-leads', 'campaign-overview', 'campaign-export'] as const;
export const COMPANY_FIELDS = { company: 'Company name', location: 'Location', employeeRange: 'Employees', status: 'Status' };
export const LEAD_FIELDS = { name: 'Lead name', role: 'Role', company: 'Company', location: 'Location', status: 'Status' };
/** Opportunity types a campaign report directory may include — campaign-scoped
 *  detections (lvl2.opportunities) and/or the lead's general market ones
 *  (lvl1_person.opportunities). Empty = the opportunities sheet is off. */
export const CAMPAIGN_OPPORTUNITY_TYPES = {
  campaign: 'Campaign opportunities',
  general: 'General opportunities',
} as const;
export type CampaignOpportunityTypeKey = keyof typeof CAMPAIGN_OPPORTUNITY_TYPES;
/** Pipeline stages the directory/export may include — same keys the status
 *  system normalizes to. Proposal/converted exist in the pipeline but the
 *  campaign reports' directory historically stops at engaged. */
export const CAMPAIGN_STATUS_SCOPE = {
  new_lead: 'New lead', enriching: 'Enriching', enriched: 'Enriched',
  outreaching: 'Outreaching', outreached: 'Outreached', engaged: 'Engaged',
} as const;
export type CampaignStatusScopeKey = keyof typeof CAMPAIGN_STATUS_SCOPE;

export interface CampaignContentConfig {
  graph: boolean;
  directory: 'none' | 'companies' | 'leads' | 'both';
  logos: boolean;
  companyFields: string[];
  leadFields: string[];
  /** Which lead statuses the directory and the data export include. */
  statuses: CampaignStatusScopeKey[];
  /** Which opportunity types the report sheet includes. Empty = off. */
  opportunities: CampaignOpportunityTypeKey[];
}
export function normalizeCampaignContent(value: unknown, template = 'campaign-companies'): CampaignContentConfig {
  const defaults: CampaignContentConfig = { graph: true, directory: template === 'campaign-overview' ? 'none' : template === 'campaign-companies' ? 'companies' : 'leads', logos: true, companyFields: Object.keys(COMPANY_FIELDS), leadFields: ['name', 'role', 'company', 'status'], statuses: Object.keys(CAMPAIGN_STATUS_SCOPE) as CampaignStatusScopeKey[], opportunities: [] };
  if (value == null) return defaults;
  const v = value as CampaignContentConfig;
  if (typeof v.graph !== 'boolean' || typeof v.logos !== 'boolean' || !['none','companies','leads','both'].includes(v.directory) || !Array.isArray(v.companyFields) || !Array.isArray(v.leadFields) || !v.companyFields.every(k => Object.hasOwn(COMPANY_FIELDS,k)) || !v.leadFields.every(k => Object.hasOwn(LEAD_FIELDS,k))) throw new Error('Invalid campaign content settings.');
  // Legacy configs (before statuses existed) fall back to the full scope —
  // their directories showed every stage.
  const statuses = Array.isArray(v.statuses) && v.statuses.length
    ? [...new Set(v.statuses)].filter(k => Object.hasOwn(CAMPAIGN_STATUS_SCOPE,k)) as CampaignStatusScopeKey[]
    : (Object.keys(CAMPAIGN_STATUS_SCOPE) as CampaignStatusScopeKey[]);
  // Opportunities are OPT-IN: configs saved before the field existed (absent
  // here) get none — existing templates must not grow a new sheet silently.
  const opportunities = Array.isArray(v.opportunities)
    ? [...new Set(v.opportunities)].filter(k => Object.hasOwn(CAMPAIGN_OPPORTUNITY_TYPES,k)) as CampaignOpportunityTypeKey[]
    : [];
  return { graph: v.graph, directory: v.directory, logos: v.logos, companyFields: [...new Set(v.companyFields)], leadFields: [...new Set(v.leadFields)], statuses, opportunities };
}
